<template>
  <div>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableRemote
      v-else
      :iconHeader="hasPermission('reports.types.add') ? 'add' : null"
      :items="displayedTypes"
      isSearchable
      isCustomIcon
      :isInfinitScroll="true"
      :headers="tableHeaders"
      :itemActions="actions"
      :title="$t('reports.types.pageName')"
      :loading="isFetching"
      :centerColumns="['action']"
      :notSortableColumns="['action']"
      :sortableColumns="sortableColumns"
      :pagination="pagination"
      hideDefaultFooter
      @iconAction="onClickAction"
      @click-icon-header="onClickAdd"
      @sendQueryParams="onQueryParams"
    >
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </VTableRemote>
  </div>
</template>


<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import ReportForm from '@/components/forms/ReportForm';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import perpage from '@/constants/pagination';

export default {
    components: {
        VTableRemote,
    },
    data: () => ({
        types: [],
        pagination: {},
        queryParams: {
            page: 1,
            perpage,
            search: '',
        },
        isFetching: false,
        isInitializing: false,
    }),
    computed: {
        ...mapGetters([
            'lang',
            'hasPermission',
            'teamMainLang',
            'userLang',
        ]),
        tableHeaders() {
            return [
                'label', 'name',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        sortableColumns() {
            return this.tableHeaders.map(column => this.$options.filters.lowercase(column.value));
        },
        displayedTypes() {
            return this.types.map(type => ({
                label: !type.label[this.userLang] || !type.label[this.userLang].length
                    ? type.label[this.teamMainLang]
                    : type.label[this.userLang],
                name: type.name ?? '',
                id: type.id,
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('reports.types.edit')) {
                actions.push({
                    name: this.$t('forms.labels.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('reports.types.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        this.isInitializing = true;
        await this.getTypes();
        this.isInitializing = false;
    },
    methods: {
        async getTypes(nextPage = 1) {
            if (this.isFetching) return;
            if (nextPage === 1 && this.queryParams.page === 1) {
                this.isFetching = true;
                this.queryParams.page = nextPage;
                const { data } = await this.$rq.getReportTypes(this.queryParams);
                this.types = [...data.types];
                this.pagination = data.pagination;
                this.isFetching = false;
            }

            if (nextPage <= this.queryParams.page) {
                return;
            }

            if (nextPage <= this.queryParams.page) return;
            this.isFetching = true;
            this.queryParams.page = nextPage;
            const { data } = await this.$rq.getReportTypes(this.queryParams);
            this.types = [...this.types, ...data.types];
            this.pagination = data.pagination;
            this.isFetching = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('reports.types.add'),
                showLangs: true,
                isForm: true,
                component: Vue.component('ReportForm', ReportForm),
                onConfirm: requestPayload => this.addType(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
                return;
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getReportType(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('reports.types.update'),
                    showLangs: true,
                    isForm: true,
                    component: Vue.component('ReportForm', ReportForm),
                    payload: { reportData: cloneDeep(data), isEdition: true },
                    onConfirm: requestPayload => this.updateType(item.id, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('reports.types.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('reports.types.confirmRemove') },
                    onConfirm: () => this.removeType(item),
                    isForm: false,
                });
            }
        },
        async addType(requestPayload) {
            await this.$rq.addReportType(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('reports.types.added', { name: requestPayload.label[this.lang] }),
            });
            await this.onQueryParams({});
        },
        async updateType(id, requestPayload) {
            if (!requestPayload) return;
            await this.$rq.updateReportType(id, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('reports.types.updated', { name: requestPayload.label[this.lang] }),
            });
            await this.onQueryParams({});
        },
        async removeType(type) {
            if (!type.name) {
                await this.$rq.removeReportType(type.id);
                this.$toasted.global.toast_success({
                    message: this.$t('reports.types.removed', { name: type.label[this.lang] }),
                });
                return this.onQueryParams({});
            }
            return this.$toasted.global.toast_info({
                message: this.$t('reports.types.canNotBeRemoved', { name: type.label[this.lang] }),
            });
        },
        async onQueryParams(queryParams) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                search: queryParams?.search || '',
                page: 1,
            };
            this.pagination = {};
            this.types = [];
            await this.getTypes();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition && !this.isFetching) {
                await this.getTypes(this.queryParams.page + 1);
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },

    },
};
</script>
